import styled from '@emotion/styled'
import * as React from 'react'
import { useInView } from 'react-intersection-observer'
import { useBallotContext } from '../../contexts'
import {
  CantonId,
  CantonState,
  lookupCantonByFeatureId,
  mkMunicipalityId,
  MunicipalityId,
  MunicipalityState,
} from '../../domain/index'
import { color, white } from '../../materials'
import { FeaturesLoader } from '@/components/ballot-map/features'
import { areaFill } from '@/components/MapsShared/shared'
import { BallotFeature } from '@/components/ballot-map/use-geodata'
import * as F from 'fp-ts'

export interface CantonalMapSvgProps {
  date: Date
  width: number
  cantonState: CantonState
  municipalityData: Array<MunicipalityState>
  selectedMunicipalityId?: MunicipalityId
  onSelectMunicipality?: (id: MunicipalityId) => void
  onResetSelection?: () => void
}

const CantonalMapSvg = React.memo((props: CantonalMapSvgProps) => <CantonalMapWithData {...props} />)

const CantonalMapWithData = React.memo(
  ({
    date,
    width: widthRaw,
    cantonState,
    municipalityData,
    selectedMunicipalityId,
    onSelectMunicipality,
    onResetSelection,
  }: CantonalMapSvgProps) => {
    const [ref, inView] = useInView({ rootMargin: '40px', triggerOnce: true })
    const { ballotVariant } = useBallotContext()

    // If the map is interactive, we need a bit of padding for the drop shadow
    const padding = onSelectMunicipality || onResetSelection ? 4 : 0
    const { width, height, strokeWidth } = useMapProportions(widthRaw)
    const filterMunicipalities = useLookupMunicipalityFeatureCallback(cantonState.id)
    const fill = React.useCallback(
      ({ id }: { id?: string | number }): string =>
        F.function.pipe(
          municipalityData,
          F.array.findFirst((x) => x.id === id),
          F.option.fold(
            () => color.grey['300'],
            ({ ballotState }) => areaFill(ballotState, false, ballotVariant),
          ),
        ),
      [ballotVariant, municipalityData],
    )

    const onMouseEnter = React.useCallback(
      (id: number) => {
        if (onSelectMunicipality) {
          onSelectMunicipality(mkMunicipalityId(id))
        }
      },
      [onSelectMunicipality],
    )

    return (
      <Svg ref={ref} width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
        {inView && (
          <g transform={`translate(${padding}, ${padding})`}>
            <FeaturesLoader
              year={date.getUTCFullYear()}
              feature={'municipalities'}
              featurePredicate={filterMunicipalities}
              width={width - 2 * padding}
              height={height - 2 * padding}
              fill={fill}
              stroke={constWhite}
              strokeWidth={strokeWidth}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onResetSelection}
              activeFeature={selectedMunicipalityId}
            />
          </g>
        )}
      </Svg>
    )
  },
)

export default CantonalMapSvg

// -----------------------------------------------------------------------------

function useLookupMunicipalityFeatureCallback(cantonId: CantonId) {
  return React.useCallback(
    (feature: BallotFeature) => {
      return F.function.pipe(
        F.option.fromNullable(feature.properties),
        F.option.chain((p) => F.option.fromNullable(p.parentId)),
        F.option.map((id) => {
          const canton = lookupCantonByFeatureId(id)
          return canton.cantonId === cantonId
        }),
        F.option.getOrElse(() => false),
      )
    },
    [cantonId],
  )
}

function useMapProportions(targetWidth: number) {
  return React.useMemo(() => {
    // Limit the maximal map size
    const width = Math.min(targetWidth, 640)

    return {
      width,
      height: Math.min(width, 500),
      // 1.2 looks good on large maps, less than 0.6 is too thin
      strokeWidth: () => Math.min(1.2, Math.max(0.6, width / 500)),
    }
  }, [targetWidth])
}

// -----------------------------------------------------------------------------

const Svg = styled('svg')`
  margin: 0 auto;
  -webkit-touch-callout: none;
  user-select: none;

  path {
    stroke-linejoin: round;
  }
`

const constWhite = F.function.constant(white)
